import MainLayout from "../../../layouts/MainLayout";
import { Button, ButtonGroup, Col, Container, Input, Label, Row } from "reactstrap";
import FilePicker from "../../../components/Inputs/FilePicker";
import { MdRemoveRedEye } from "react-icons/md";
import DefaultButton from "../../../components/Buttons/Default";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL, STORAGE_URL } from "../../../utils/constants";
import { toast } from "react-toastify";
import { generateKey, tField } from "../../../utils/helpers";
import Select from "react-select";
import { SelectCustomStyles } from "../../../utils/styles";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import i18n from 'i18next';

function CreatePropertyPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user'));
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [storeLoading, setStoreLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadLength, setUploadLength] = useState(0);
  const [imagesThumbnails, setImagesThumbnails] = useState([]);
  const [isEditMode, setIsEditMode] = useState(!!location.state?.property);
  const [editableProperty, setEditableProperty] = useState(location.state?.property || null);
  const [state, setState] = useState({
    phone_number: user?.phone_number || '',
    is_premium: false,
    property_for: '',
    category_id: '',
    city_id: '',
    price: '',
    description: ''
  });
  const tempIdRef = useRef(null);
  const [filePickerResetKey, setFilePickerResetKey] = useState(0);
  const navigate = useNavigate();

  const getCategories = () => {
    fetch(API_URL+'/categories', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {      
      if (data.success) {        
        setCategories(data.categories);
        getStates();
      }
    })
  }

  const getStates = () => {
    fetch(API_URL+'/states', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        setStates(data.states);
      }
    })
  }

  useEffect(() => {
    if (isEditMode && editableProperty) {
      setState({
        phone_number: editableProperty.phone_number,
        is_premium: editableProperty.is_premium,
        property_for: editableProperty.property_for,
        category_id: editableProperty.category_id,
        city_id: editableProperty.city_id,
        price: editableProperty.price,
        description: editableProperty.description_ar
      });
    }

    getCategories();
  }, []);

  const uploadFiles = useCallback(async (fileList) => {
    if(!fileList || !fileList.length) return ;
    setUploadLoading(true);

    setUploadLength(fileList.length);
    
    const form = new FormData();
    for (const f of fileList) form.append('files[]', f);
    if(tempIdRef.current) form.append('temp_id', tempIdRef.current);
    if(isEditMode && editableProperty) form.append('property_id', editableProperty.id);

    try {
      const res = await fetch(API_URL+'/user/properties/files/upload', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: form
      });
      const json = await res.json();
      if(!json || !json.success) {
        toast.error(json?.message || 'فشل في رفع الملفات');
        return;
      }

      tempIdRef.current = json.temp_id || tempIdRef.current;
      const newFiles = (json.files || []).map(f => ({
        id: f.image_id,
        path: f.image_path
      }));
      setImagesThumbnails(prev => [...prev, ...newFiles]);
      setFilePickerResetKey(prev => prev + 1);
      setUploadLoading(false);
    } catch (err) {
      console.error(err);
      toast.error('خطأ أثناء رفع الملفات');
      setUploadLoading(false);
    }
  }, [localStorage.getItem('token')]);

  const removeFile = async (imageId, imagePath) => {
    setImagesThumbnails(prev => prev.filter(img => img.id !== imageId));
    // filter editableProperty.videos or images and remove by id
    if (editableProperty) {
      setEditableProperty(prev => ({
        ...prev,
        images: prev.images?.filter(img => img.id !== imageId) || [],
        videos: prev.videos?.filter(vid => vid.id !== imageId) || []
      }));
    }

    try {
      await fetch(API_URL+'/user/properties/files/destroy', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('token')
        },
        body: JSON.stringify({
          id: imageId,
          path: imagePath
        })
      });
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  }

  const handleImagesChange = useCallback(
    (files) => {
      const arr = Array.from(files || []);

      uploadFiles(arr);
    }, [uploadFiles]
  );

  const store = () => {
    setStoreLoading(true);
    if(!state?.price) {
      toast.error('الرجاء إدخال السعر');
      setStoreLoading(false);
      return;
    }

    if(state?.property_for === 'rent' && state?.price?.length < 3) {
      toast.error('لا يمكن أن يكون سعر الإيجار أقل من 3 أرقام');
      setStoreLoading(false);
      return;
    }

    if(state?.property_for === 'sale' && state?.price?.length < 5) {
      toast.error('لا يمكن أن يكون سعر البيع أقل من 5 أرقام');
      setStoreLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('temp_id', tempIdRef.current);
    formData.append('phone_number', state.phone_number);
    formData.append('is_premium', state.is_premium);
    formData.append('property_for', state.property_for);
    formData.append('category_id', state.category_id);
    formData.append('city_id', state.city_id);
    formData.append('price', Number(String(state.price ?? '').replace(/,/g, '')) || '');
    formData.append('description', state.description);
    
    const URL = isEditMode && editableProperty ? API_URL+'/user/properties/update/'+editableProperty.id : API_URL+'/user/properties/store';

    fetch(URL, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        setState({
          phone_number: '',
          is_premium: false,
          property_for: '',
          category_id: '',
          state_id: '',
          city_id: '',
          price: '',
          description: ''
        });
        navigate('/my_properties?p='+generateKey());
      } else {
        toast.error(data.message || 'حدث خطأ أثناء إضافة الإعلان');
      }
      setStoreLoading(false);
    })
    .catch(error => {
      console.error('Error:', error);
      setStoreLoading(false);
    });
  }

  const convertToEnglishDigits = (value) => {
    value = value
      .replace(/[٠-٩]/g, d => d.charCodeAt(0) - 0x0660)
      .replace(/[۰-۹]/g, d => d.charCodeAt(0) - 0x06F0)
      .replace(/[^0-9]/g, '');

    // format with commas
    value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return value;
  };

  return(
    <MainLayout isTopBar screenTitle={isEditMode ? t('create_property.titles.edit') : t('create_property.titles.create')}>
      <Container className="py-4 mb-5">
        <Row className="justify-content-center">
          <Col md="8">
            {isEditMode ? <h4 className="mb-3">{ t('create_property.titles.edit') }</h4> : <h4 className="mb-3">{ t('create_property.titles.create') }</h4>}

            <div>
              <div className="mb-4">
                <Label>{ t('create_property.fields.images.label') }: </Label>
                <FilePicker 
                  onChange={handleImagesChange} 
                  images={imagesThumbnails} 
                  onRemove={removeFile}
                  uploadLoading={uploadLoading}
                  uploadLength={uploadLength}
                  resetKey={filePickerResetKey}
                />
                <small className="text-muted" style={{marginTop: '-2px', display: 'block'}}>
                  إختيار صور ذات جودة عالية قد يزيد من فرص بيع أو تأجير العقار.
                </small>

                {isEditMode && editableProperty &&
                  <div className="d-flex file-picker-files mt-2 flex-wrap">
                    {editableProperty?.images?.length > 0 && editableProperty?.images.map((data) => (
                      <div className="file-picker-files-preview me-2 mb-2" key={data.id}>
                        <img
                          src={STORAGE_URL+data.image_path}
                          alt={`Preview ${data.id}`}
                          className="border"
                          style={{ maxWidth: 200, maxHeight: 150, display: 'block', objectFit: 'cover' }}
                        />
            
                        <IoMdClose
                          className="file-picker-files-preview-close"
                          size={20}
                          onClick={() => removeFile(data.id, data.image_path)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    ))}

                    {editableProperty?.videos?.length > 0 && editableProperty?.videos.map((data) => (
                      <div className="file-picker-files-preview me-2 mb-2" key={data.id}>
                        <img
                          src={STORAGE_URL+data.thumbnail_path}
                          alt={`Preview ${data.id}`}
                          className="border"
                          style={{ maxWidth: 200, maxHeight: 150, display: 'block', objectFit: 'cover' }}
                        />
            
                        <IoMdClose
                          className="file-picker-files-preview-close"
                          size={20}
                          onClick={() => removeFile(data.id, data.thumbnail_path)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    ))}
                  </div>
                }
              </div>

              <div className="mb-4">
                <Label>{ t('create_property.fields.phone_number.label') }: </Label>
                <Input 
                  type='text' 
                  placeholder={ t('create_property.fields.phone_number.placeholder') }
                  value={state.phone_number}
                  onChange={(e) => setState({ ...state, phone_number: e.target.value })}
                />
                <small className="text-muted">{ t('create_property.fields.phone_number.help_text') }</small>
              </div>

              {isEditMode ? null :
                <div className="mb-4">
                  <Input 
                    type="checkbox"
                    checked={state.is_premium}
                    onChange={(e) => setState({ ...state, is_premium: e.target.checked })}
                  />
                  {' '}
                  { t('create_property.fields.is_premium.label') }
                </div>
              }

              <div className="mb-4">
                <Label>{ t('create_property.fields.purpose.label') }: </Label>
                <ButtonGroup className='d-flex'>
                  <Button 
                    color='primary' 
                    outline 
                    active={state.property_for === 'sale'}
                    onClick={() => setState({ ...state, property_for: 'sale' })}
                  >
                    { t('create_property.fields.purpose.options.sale') }
                  </Button>
                  <Button 
                    color='primary' 
                    outline
                    active={state.property_for === 'rent'}
                    onClick={() => setState({ ...state, property_for: 'rent' })}
                  >
                    { t('create_property.fields.purpose.options.rent') }
                  </Button>
                  <Button 
                    color='primary' 
                    outline
                    active={state.property_for === 'exchange'}
                    onClick={() => setState({ ...state, property_for: 'exchange' })}
                  >
                    { t('create_property.fields.purpose.options.exchange') }
                  </Button>
                </ButtonGroup>
              </div>

              <Row>
                <Col xs="12" className='mb-4'>
                  <Label>{ t('create_property.fields.category.label') }</Label>
                  <Select 
                    placeholder={ t('create_property.fields.category.placeholder') } 
                    options={categories.map(cat => ({ value: cat.id, label: tField(cat, 'name') }))} 
                    onChange={(option) => setState({ ...state, category_id: option.value })}
                    styles={SelectCustomStyles}
                    isSearchable={false}
                    value={state.category_id ? { value: state.category_id, label: categories.find(cat => cat.id === state.category_id)?.['name_' + i18n.language] } : null}
                  />
                </Col>
                <Col xs="12" className='mb-4'>
                  <Label>{ t('create_property.fields.city.label') }</Label>
                  <Select 
                    placeholder={ t('create_property.fields.city.placeholder') } 
                    options={states.map(state => ({
                      label: tField(state, 'name'),
                      options: state.cities.map(city => ({
                        value: city.id,
                        label: tField(city, 'name'),
                      })),
                    }))}
                    onChange={(option) => setState({ ...state, city_id: option.value })}
                    styles={SelectCustomStyles}
                    value={states.flatMap(s => s.cities).find(city => city.id === state.city_id) ? { value: state.city_id, label: tField(states.flatMap(s => s.cities).find(city => city.id === state.city_id), 'name') } : null}
                  />
                </Col>
              </Row>

              <div className="mb-4">
                <Label>{ t('create_property.fields.price.label') }: </Label>
                <Input 
                  type='text' 
                  inputMode='numeric'
                  value={state.price || ''}
                  onChange={(e) => setState({ ...state, price: convertToEnglishDigits(e.target.value) })}
                />
              </div>

              <div className="mb-4">
                <Label>{ t('create_property.fields.description.label') }: </Label>
                <Input 
                  type='textarea' 
                  value={state.description}
                  onChange={(e) => setState({ ...state, description: e.target.value })}
                  rows={6}
                />
              </div>

              <div className="mb-4 text-center">
                <DefaultButton 
                  color='primary' 
                  className="px-5" 
                  onClick={store} 
                  loading={storeLoading}
                  disabled={uploadLoading || storeLoading || !state.category_id || !state.city_id || !state.description || !state.property_for}
                >
                  {isEditMode ? t('create_property.buttons.edit') : t('create_property.buttons.create') }
                </DefaultButton>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </MainLayout>
  );
}

export default CreatePropertyPage;